import { createContext, useContext, useEffect, useMemo, useReducer, type ReactNode } from "react";
import { responseOptions, traceNodes } from "../data/seed";
import type {
  OverridableReadinessRuleId,
  ResponseOption,
  TraceNode,
} from "../domain";
import {
  initialScenarioState,
  scenarioReducer,
  type ScenarioState,
} from "./scenarioMachine";
import {
  persistScenarioState,
  readPersistedScenarioState,
} from "./scenarioPersistence";

type ApprovalInput = {
  actor: string;
  approverId: string;
  approverName: string;
  canApproveSensitiveChanges: boolean;
  decision: "approved" | "rejected";
  reason: string;
  responseId: ResponseOption["id"];
};

type ScenarioContextValue = ScenarioState & {
  selectedNode: TraceNode;
  selectedDecision: ResponseOption | null;
  recoveryPercent: number;
  remainingUsers: number;
  startScenario: (actor: string) => void;
  resetScenario: () => void;
  selectTraceNode: (nodeId: string) => void;
  closeDrawer: () => void;
  linkEvidence: (evidenceId: string, actor: string) => void;
  assignIncidentOwner: (
    actor: string,
    ownerId: string,
    ownerName: string,
    reason: string,
  ) => void;
  recordReadinessOverride: (
    ruleId: OverridableReadinessRuleId,
    actor: string,
    reason: string,
    canApproveSensitiveChanges: boolean,
  ) => void;
  decideResponse: (input: ApprovalInput) => void;
  verifyRecovery: (
    actor: string,
    serviceRecovered: boolean,
    endpointReregistered: boolean,
    reason: string,
  ) => void;
  reviseResponse: (actor: string) => void;
  recordArtifact: (artifact: "teams" | "report" | "handoff", actor: string) => void;
  openGuide: () => void;
  closeGuide: () => void;
  nextGuideStep: () => void;
  previousGuideStep: () => void;
};

const ScenarioContext = createContext<ScenarioContextValue | null>(null);

export function ScenarioProvider({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(
    scenarioReducer,
    initialScenarioState,
    readPersistedScenarioState,
  );
  const selectedNode = traceNodes.find((node) => node.id === state.selectedTraceNodeId) ?? traceNodes[0];
  const selectedDecision = responseOptions.find((decision) => decision.id === state.decisionId) ?? null;

  useEffect(() => {
    persistScenarioState(state);
  }, [state]);

  const value = useMemo<ScenarioContextValue>(
    () => ({
      ...state,
      selectedNode,
      selectedDecision,
      recoveryPercent: selectedDecision?.recoveryPercent ?? (state.stage === "calm" ? 100 : 63),
      remainingUsers: selectedDecision?.remainingUsers ?? (state.stage === "calm" ? 0 : 183),
      startScenario: (actor) => dispatch({ type: "start", actor }),
      resetScenario: () => dispatch({ type: "reset" }),
      selectTraceNode: (nodeId) => dispatch({ type: "select-node", nodeId }),
      closeDrawer: () => dispatch({ type: "close-drawer" }),
      linkEvidence: (evidenceId, actor) => dispatch({ type: "link-evidence", evidenceId, actor }),
      assignIncidentOwner: (actor, ownerId, ownerName, reason) => dispatch({
        type: "record-interaction",
        event: {
          type: "owner-assigned",
          actor,
          incidentId: "INC-2048",
          ownerId,
          ownerName,
          reason,
        },
      }),
      recordReadinessOverride: (
        ruleId,
        actor,
        reason,
        canApproveSensitiveChanges,
      ) => dispatch({
        type: "record-readiness-override",
        canApproveSensitiveChanges,
        event: {
          type: "readiness-override-recorded",
          actor,
          incidentId: "INC-2048",
          ruleId,
          reason,
        },
      }),
      decideResponse: (input) => dispatch({
        type: "decide-response",
        canApproveSensitiveChanges: input.canApproveSensitiveChanges,
        event: {
          type: "response-decided",
          actor: input.actor,
          incidentId: "INC-2048",
          decision: input.decision,
          responseId: input.responseId,
          approverId: input.approverId,
          approverName: input.approverName,
          reason: input.reason,
        },
      }),
      verifyRecovery: (
        actor,
        serviceRecovered,
        endpointReregistered,
        reason,
      ) => dispatch({
        type: "record-interaction",
        event: {
          type: "recovery-verified",
          actor,
          incidentId: "INC-2048",
          serviceRecovered,
          endpointId: "TEL-0318",
          endpointReregistered,
          reason,
        },
      }),
      reviseResponse: (actor) => dispatch({ type: "revise-response", actor }),
      recordArtifact: (artifact, actor) => dispatch({ type: "record-artifact", artifact, actor }),
      openGuide: () => dispatch({ type: "open-guide" }),
      closeGuide: () => dispatch({ type: "close-guide" }),
      nextGuideStep: () => dispatch({ type: "guide-next" }),
      previousGuideStep: () => dispatch({ type: "guide-back" }),
    }),
    [selectedDecision, selectedNode, state],
  );

  return <ScenarioContext.Provider value={value}>{children}</ScenarioContext.Provider>;
}

export function useScenario() {
  const context = useContext(ScenarioContext);
  if (!context) throw new Error("useScenario must be used inside ScenarioProvider");
  return context;
}
